import BasePresenter from '@src/js/separate-logic/base-presenter';
import { dispatchProps } from '@src/js/separate-logic/decorate/dispatchProps';
import styles from '@editor/src/page/index/components/canvas/slide.css';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { reaction } from 'mobx';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';

export default class SlidePresenter extends BasePresenter {
    public domRefs: any;
    public pathRefs: any;
    private wrapperRefs: any;
    private mounted: boolean;
    private changeHideTimeout;
    private refreshReaction;
    private taskKey;

    public init(props: any) {
        this.domRefs = {};
        this.wrapperRefs = {};
        this.pathRefs = {};
        this.taskKey = `render_${props.slideModel?.sdkData?.uniqueID ?? props.slideModel?.sdkData?.id}`;
    }

    public shouldComponentUpdate(nextProps): boolean {
        if (nextProps.isShown !== this.props.isShown) {
            this.onPropsIsShownChange(nextProps);
        }

        if (nextProps.isShown) {
            if (nextProps.scale !== this.props.scale) {
                return true;
            }
        }

        if (nextProps.slideModel.children.length !== this.props.slideModel.children.length) {
            return true;
        }

        if (nextProps.slideWidth !== this.props.slideWidth) {
            return true;
        }
        return nextProps.slideHeight !== this.props.slideHeight;
    }

    public componentDidMount(): void {
        const { slideModel } = this.props;

        this.updateSlideElementRefs();

        this.mounted = true;

        this.refreshReaction = reaction(() => {
            const isAnimationEditorShown = this.getRightSideBarManager()?.isAnimationEditorShown;
            return {
                isAnimationEditorShown,
                displayChildrenElement: slideModel.displayChildrenElement,
            };
        }, this.updateComponent);

        slideModel.changeSetEngine.addChangesetObserver(this.updateComponent);

        this.containerRef?.addEventListener('mousedown', this.handleMouseDownOnCanvas, true);
        this.containerRef?.addEventListener('touchstart', this.handleMouseDownOnCanvas, true);
    }

    public componentDidUpdate() {
        this.updateSlideElementRefs();
    }

    public componentWillUnmount(): void {
        this.mounted = false;

        this.refreshReaction?.();

        const { slideModel } = this.props;
        slideModel.changeSetEngine.removeChangesetObserver(this.updateComponent);

        clearInterval(this.changeHideTimeout);
        this.containerRef?.removeEventListener('mousedown', this.handleMouseDownOnCanvas, true);
        this.containerRef?.removeEventListener('touchstart', this.handleMouseDownOnCanvas, true);

        TaskManager.cancelTaskByKey(this.taskKey);
    }

    @dispatchProps()
    public setDomRef = (key, ref) => {
        this.domRefs[key] = ref;
    };

    @dispatchProps()
    public setWrapperRef = (key, ref) => {
        this.wrapperRefs[key] = ref;
    };

    @dispatchProps()
    public setPathRef = (key, ref) => {
        this.pathRefs[key] = ref;
    };

    private updateComponent = () => {
        if (this.mounted) {
            const oldShown = this.props.isShown;
            this.forceUpdate(() => {
                if (!oldShown && this.props.isShown) {  // 同步一下状态
                    this.updateContainerClassName({ isShown: true });
                }
            });
        }
    };

    private get containerRef() {
        return this.getChildRef('containerRef');
    }

    private get classList() {
        return this.containerRef?.classList;
    }

    private handleMouseDownOnCanvas = () => {
    };

    private onPropsIsShownChange(nextProps) {
        this.updateContainerClassName(nextProps);

        // eslint-disable-next-line no-restricted-syntax
        for (const key in this.wrapperRefs) {
            // eslint-disable-next-line no-prototype-builtins
            if (this.wrapperRefs.hasOwnProperty(key)) {
                const element = this.wrapperRefs[key];
                element?.selectStateChange?.();
            }
        }
        if (nextProps.isShown) {
            this.getAppServices().viewableServiceLayer.switchPageMonitor.end(this.props.slideModel.key);
        }

        this.updateTaskPriority(nextProps);
    }

    private updateTaskPriority(nextProps) {
        const priority = nextProps.isShown ? TaskPriority.HIGH + 2 : TaskPriority.HIGH;

        const task = TaskManager.getTaskWithKey(this.taskKey);

        if (task) {
            task.priority = priority;
        }
    }

    private updateContainerClassName(nextProps) {
        if (!this.classList) {
            return;
        }
        if (nextProps.isShown) {
            this.classList.remove(styles.slideUndisplay);
            this.classList.add('currentSlide');

            this.updateSlideElementRefs();
        } else {
            this.classList.remove('currentSlide');
            this.classList.add(styles.slideUndisplay);
        }
    }

    private updateSlideElementRefs = () => {
        if (this.getSelectionService().currentSlide === this.props.slideModel) {
            if (this.getRightSideBarManager()?.isAnimationEditorShown) {
                this.getAnimPreviewManager()?.updateCurrentSlideElementRefs();
            }
        }
    };

    private getRightSideBarManager = () => {
        if (AppServiceProvider.getInstance().getAppServices()
            .getEntryType() !== AppEntryType.PC_EDIT) return;
        return (AppServiceProvider.getInstance()
            .getAppServices() as IPcEditorAppServices).editableServiceLayer?.rightSideBarManager;
    };

    private getAnimPreviewManager = () => (AppServiceProvider.getInstance()
        .getAppServices() as IPcEditorAppServices).viewableServiceLayer?.getEditorAnimPreviewManager();


    private getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance()
            .getAppServices() as IEditorAppServices;
    }

    private getSelectionService(): ISelectionService {
        return this.getAppServices().viewableServiceLayer.selectionService;
    }

    private getUserPriviledgeService(): IUserPriviledgeService {
        return this.getAppServices().viewableServiceLayer.userPriviledgeService;
    }
}
